/**
 * Metric Card Component
 * 
 * Displays a metric value with optional title, hint, icon, and trend indicator.
 * Used throughout the dashboard to show key performance indicators (KPIs).
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Metric title (required)
 * @param {string} props.value - Metric value to display (required)
 * @param {string} [props.hint] - Optional hint text displayed below value
 * @param {string} [props.icon] - Material Symbols icon name
 * @param {'up'|'down'|'flat'|'neutral'|''} [props.trend] - Trend direction for badge
 * @param {string} [props.trendBadge] - Text to display in trend badge (e.g., "+5%")
 * @param {string} [props.accent] - CSS variable for accent color
 * @param {string} [props.accentSoft] - CSS variable for soft accent color
 * @param {string} [props.accentStrong] - CSS variable for strong accent color
 * @param {string} [props.className] - Additional CSS classes
 * 
 * @example
 * <MetricCard
 *   title="Total Revenue"
 *   value="GHS 10,000.00"
 *   hint="Last 30 days"
 *   icon="trending_up"
 *   trend="up"
 *   trendBadge="+5%"
 * />
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';

const MetricCard = memo(({
  title,
  value,
  hint,
  icon,
  trend,
  trendBadge,
  accent,
  accentSoft,
  accentStrong,
  className = '',
}) => {
  const cardStyle = {
    '--metric-accent': accent,
    '--metric-accent-soft': accentSoft,
    '--metric-accent-strong': accentStrong,
  };

  const trendClass =
    trend && trendBadge ? `metric-trend metric-trend--${trend}` : null;

  return (
    <div
      className={`metric-card dashboard-metric-card ${className}`.trim()}
      style={cardStyle}
    >
      <div className="dashboard-metric-card__header">
        {icon ? (
          <div className="dashboard-metric-card__icon">
            <span className="material-symbols-outlined">{icon}</span>
          </div>
        ) : null}

        {trendClass ? <span className={trendClass}>{trendBadge}</span> : null}
      </div>

      <div className="metric-title">{title}</div>
      <div className="metric-value dashboard-metric-card__value">{value}</div>
      {hint ? <div className="metric-hint">{hint}</div> : null}
    </div>
  );
});

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  hint: PropTypes.string,
  icon: PropTypes.string,
  trend: PropTypes.oneOf(['up', 'down', 'flat', 'neutral', '']),
  trendBadge: PropTypes.string,
  accent: PropTypes.string,
  accentSoft: PropTypes.string,
  accentStrong: PropTypes.string,
  className: PropTypes.string,
};

MetricCard.defaultProps = {
  hint: '',
  icon: '',
  trend: '',
  trendBadge: '',
  accent: 'var(--accent)',
  accentSoft: 'rgba(37, 99, 235, 0.12)',
  accentStrong: 'rgba(37, 99, 235, 0.28)',
  className: '',
};

MetricCard.displayName = 'MetricCard';

export default MetricCard;

